<script setup>
// SICARG: cronómetro de tiempo conectado del día. Muestra cuánto lleva el
// agente en availability "online" hoy, en vivo, alimentado por el mismo
// endpoint que el reporte "Tiempos de conexión de agentes" (self-scoped).
// Se congela (no tickea) apenas deja de estar online, y vuelve a pedir un
// valor fresco al backend cuando vuelve a online, cuando la pestaña vuelve a
// estar visible, o cuando cambia el día calendario.
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { DropdownItem } from 'next/dropdown-menu/base';

const { t } = useI18n();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentUserId = useMapGetter('getCurrentUserID');

const baselineSeconds = ref(0);
const fetchedAtMs = ref(Date.now());
const displaySeconds = ref(0);
const todayKey = ref(new Date().toDateString());
const hasLoadedOnce = ref(false);

let tickTimer = null;
let resyncTimer = null;
let retryTimer = null;

const isOnline = computed(() => currentUserAvailability.value === 'online');

const startOfTodayEpoch = () =>
  Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
const nowEpoch = () => Math.floor(Date.now() / 1000);

const fetchConnectedSeconds = async () => {
  try {
    const { data } = await ReportsAPI.getAgentConnectedTime({
      from: startOfTodayEpoch(),
      to: nowEpoch(),
      userId: currentUserId.value,
    });
    // Para admins, si el account_user propio no viene primero en la
    // respuesta (el backend no filtra por user_id cuando currentUserId
    // todavía no está hidratado en el store), tomar la fila explícita del
    // usuario logueado en vez de asumir la primera del array.
    const agent =
      data.agents.find(a => a.user_id === currentUserId.value) ||
      data.agents[0];
    baselineSeconds.value = agent?.connected_seconds || 0;
    fetchedAtMs.value = Date.now();
    displaySeconds.value = baselineSeconds.value;
    hasLoadedOnce.value = true;
    clearTimeout(retryTimer);
  } catch (error) {
    // El primer fetch tras un reload de página puede competir con el
    // bootstrap de la sesión y fallar una vez. Mientras no haya habido
    // ningún fetch exitoso todavía, reintentar a los 5s en vez de esperar
    // al próximo resync (60s) — si no, el widget queda mostrando un
    // placeholder de carga hasta un minuto entero, que se lee como si el
    // contador se hubiera reseteado.
    if (!hasLoadedOnce.value) {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(fetchConnectedSeconds, 5000);
    }
  }
};

const tick = () => {
  const currentDateKey = new Date().toDateString();
  if (currentDateKey !== todayKey.value) {
    todayKey.value = currentDateKey;
    fetchConnectedSeconds();
    return;
  }

  // Antes de la primera respuesta del backend no hay baseline real todavía
  // — no avanzar el reloj localmente para no mostrar un valor inventado
  // que luego "salte" cuando llegue el dato real.
  if (isOnline.value && hasLoadedOnce.value) {
    displaySeconds.value =
      baselineSeconds.value +
      Math.floor((Date.now() - fetchedAtMs.value) / 1000);
  }
};

const formattedTime = computed(() => {
  const total = Math.max(displaySeconds.value, 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours, minutes, seconds]
    .map(unit => String(unit).padStart(2, '0'))
    .join(':');
});

watch(isOnline, online => {
  if (online) fetchConnectedSeconds();
});

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') fetchConnectedSeconds();
};

onMounted(() => {
  fetchConnectedSeconds();
  tickTimer = setInterval(tick, 1000);
  resyncTimer = setInterval(fetchConnectedSeconds, 60000);
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  clearInterval(tickTimer);
  clearInterval(resyncTimer);
  clearTimeout(retryTimer);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <DropdownItem>
    <div class="flex-grow min-w-0">
      {{ t('SIDEBAR.CONNECTED_TIME.TEXT') }}
    </div>
    <span class="font-mono text-sm text-n-slate-11 tabular-nums shrink-0">
      {{ hasLoadedOnce ? formattedTime : '—' }}
    </span>
  </DropdownItem>
</template>
